import React, { useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  IconButton, 
  Container,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  RateReview as ReviewIcon,
  Description as ReportIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  QuestionAnswer as QuestionIcon,
  Category as CategoryIcon,
  ListAlt as QuestionnaireIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';

const drawerWidth = 240;

const AuditLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Toggle drawer
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Handle user menu open
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  // Handle user menu close
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/audit/my-cases',
        roles: ['administrator', 'manager', 'reviewer']
      }
    ];

    // Add manager-specific items
    if (['administrator', 'manager'].includes(user?.role)) {
      items.push(
        {
          text: 'Firms',
          icon: <BusinessIcon />,
          path: '/audit/firms',
          roles: ['administrator', 'manager']
        },
        {
          text: 'Cases',
          icon: <AssignmentIcon />,
          path: '/audit/cases',
          roles: ['administrator', 'manager']
        },
        {
          text: 'Reviewers',
          icon: <PersonAddIcon />,
          path: '/audit/reviewers',
          roles: ['administrator', 'manager']
        },
        {
          text: 'Questions',
          icon: <QuestionIcon />,
          path: '/audit/questions',
          roles: ['administrator', 'manager']
        },
        {
          text: 'Categories',
          icon: <CategoryIcon />,
          path: '/audit/categories',
          roles: ['administrator', 'manager']
        },
        {
          text: 'Questionnaires',
          icon: <QuestionnaireIcon />,
          path: '/audit/questionnaires',
          roles: ['administrator', 'manager']
        }
      );
    }

    // Add reviewer-specific items
    if (['administrator', 'manager', 'reviewer'].includes(user?.role)) {
      items.push({
        text: 'My Reviews',
        icon: <ReviewIcon />,
        path: '/audit/my-reviews',
        roles: ['administrator', 'manager', 'reviewer']
      });
    }

    return items;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Audit Management System
          </Typography>
          
          {/* User Menu */}
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={user?.name} src={user?.avatar || '/static/images/avatar.jpg'} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/profile'); }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ...(!open && {
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              width: (theme) => theme.spacing(7),
              [(theme) => theme.breakpoints.up('sm')]: {
                width: (theme) => theme.spacing(9),
              },
            }),
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {getNavigationItems().map((item) => (
              <ListItem 
                button 
                key={item.text}
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItem>
            ))}
          </List>
          <Divider />
          {user?.role === 'administrator' && (
            <Box sx={{ p: 2, display: open ? 'block' : 'none' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/admin')}
                startIcon={<DashboardIcon />}
              >
                Admin Dashboard
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
      
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default AuditLayout;
